import React, { useContext, useState, Dispatch, useEffect } from 'react';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import { ProjectContext } from '../../../contexts/ProjectContext';
import FindingCard from '../../Inspections/Findings/FindingCard';
import Loading from '../../Shared/ResourceIndex/Loading';
import {
  FormContext,
  FormSchemaFields,
  FormContextProvider,
} from '@stormwater-software/lib-form';
import Pagination from 'react-js-pagination';
import { correctiveActionsFindingsApi } from '../../../utils/api/correctiveActionsFindingsApi';
import Select from 'react-select';

const controlField = 'findingFilter';
const statusField = 'statusFilter';

const InspectionTemplateTypeFilter = (props: {
  selectedValues: any;
  handleChange: any;
  options: { label: string; value: string }[];
}) => {
  const modifiedOptions = [{ label: 'All', value: 'all' }, ...props.options];
  const getSelectedOptions = () => {
    return modifiedOptions.filter((opt) =>
      props.selectedValues.includes(opt.value),
    );
  };

  const onChange = (selected: any) => {
    if (!selected || !Array.isArray(selected)) {
      props.handleChange([]);
      return;
    }

    const hasSelectAll = selected.some((opt) => opt.value === 'all');
    let finalValues;
    if (hasSelectAll) {
      finalValues = props.options.map((opt) => opt.value);
    } else {
      finalValues = selected.map((opt) => opt.value);
    }

    props.handleChange(finalValues);
  };

  return (
    <Select
      styles={{
        control: (provided: any) => ({
          ...provided,
          backgroundColor: 'white',
          borderColor: '#b1b8b6',
          borderRadius: '2px',
          padding: '1px',
          height: '41.77px',
          minWidth: '250px',
          boxShadow: 'none',
          flexWrap: 'wrap',
          '&:hover': {
            borderColor: '#48af98',
          },
        }),
        container: (provided: any) => ({
          ...provided,
          height: '41.77px',
        }),
        indicatorSeparator: (provided: any) => ({
          ...provided,
          display: 'none',
        }),
        dropdownIndicator: (provided: any) => ({
          ...provided,
          padding: '4px',
          color: '#002230',
        }),
        valueContainer: (provided: any) => ({
          ...provided,
          padding: '0px 2px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: '2px',
          maxHeight: '38px',
          maxWidth: '250px',
          overflowY: 'auto',
        }),
        menu: (provided: any) => ({
          ...provided,
          backgroundColor: 'white',
          color: '#002230',
          marginTop: 0,
          zIndex: 9999,
          borderRadius: 0,
        }),
        menuList: (provided: any) => ({
          ...provided,
          padding: 0,
          borderRadius: 0,
        }),
        option: (provided: any, state: any) => ({
          ...provided,
          backgroundColor: state.isFocused ? '#f9f5ed' : 'white',
          color: '#002230',
          cursor: 'pointer',
          padding: '5px',
          '&:hover': {
            backgroundColor: '#48af98',
            color: 'white',
          },
          minHeight: '30px',
        }),
        noOptionsMessage: (provided: any) => ({
          ...provided,
          color: '#002230',
          padding: '10px',
          fontSize: '14px',
          borderRadius: 0,
        }),
        multiValue: (provided: any) => ({
          ...provided,
          backgroundColor: '#e8e4df',
          borderRadius: '5px',
          height: '24px',
          margin: '0px',
        }),
        multiValueLabel: (provided: any) => ({
          ...provided,
          color: '#002230',
          fontSize: '14px',
          padding: '2px 6px',
        }),
        multiValueRemove: (provided: any) => ({
          ...provided,
          cursor: 'pointer',
          color: '#002230',
          ':hover': {
            borderRadius: '5px',
            color: 'white',
          },
        }),
      }}
      value={getSelectedOptions()}
      onChange={onChange}
      options={modifiedOptions}
      isMulti={true}
      classNamePrefix='react-select'
      isSearchable={true}
      placeholder='Select Inspection Type...'
    />
  );
};

const FindingFilter = (props: {
  handleChange: Dispatch<string>;
  options: { label: string; value: string }[];
}) => {
  const formContext = useContext(FormContext);

  const schema = {
    [controlField]: {
      controlType: UIControlType.select,
      placeholder: 'Finding Types',
      className: 'filter small',
      options: [{ label: 'All Findings', value: 'all' }, ...props.options],
      style: {
        marginRight: '10px',
        marginLeft: '2px',
      },
    },
  };

  return (
    <FormSchemaFields
      className='filter-form'
      formData={formContext.value}
      onChange={(field, val) => {
        formContext.set(field, val);
        props.handleChange(val);
      }}
      schema={schema}
    />
  );
};

const FindingStatusFilter = (props: {
  handleChange: Dispatch<string>;
  options: { label: string; value: string }[];
}) => {
  const formContext = useContext(FormContext);

  const schema = {
    [statusField]: {
      controlType: UIControlType.select,
      placeholder: 'Findings Status',
      className: 'filter small',
      options: [...props.options],
      style: {
        marginRight: '9px',
      },
    },
  };

  return (
    <FormSchemaFields
      className='filter-form'
      formData={formContext.value}
      onChange={(field, val) => {
        formContext.set(field, val);
        props.handleChange(val);
      }}
      schema={schema}
    />
  );
};

export default function FindingListing(_props: {
  showFindings: string;
  inspectionId: number;
  Finding: string;
}) {
  const projectContext = useContext(ProjectContext);

  const [filter, setFilter] = useState<string>('all');
  const [templateTypes, setTemplateTypes] = useState<string[]>([]);
  const [status, setStatus] = useState<string>('all');
  const [page, setPage] = useState<number>(1);
  const [totalFindingModelCount, setTotalFindingModelCount] =
    useState<number>(10);
  const [correctiveActionsQuery, setCorrectiveActionsQuery] = useState<any>();
  const [findingFilterOptions, setFindingFilterOptions] =
    useState<Array<{ label: string; value: string }>>();
  const [inspectionTemplateTypeOptions, setInspectionTemplateTypeOptions] =
    useState<Array<{ label: string; value: string }>>();
  const [findingStatusOptions] = useState<
    Array<{ label: string; value: string }>
  >([
    {
      label: 'All Findings',
      value: 'all',
    },
    {
      label: 'Open Findings',
      value: 'open',
    },
    {
      label: 'Closed Findings',
      value: 'closed',
    },
  ]);
  const [perPageDataCount, setperPageDataCount] = useState<number>(10);

  const setPageValue = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const fetchFindings = async () => {
    const projectId: number = projectContext.project
      ? projectContext.project.id
      : 0;
    const data: any = await correctiveActionsFindingsApi.index(
      projectId,
      filter,
      status,
      templateTypes,
      page,
      perPageDataCount,
    );

    setCorrectiveActionsQuery(data);
  };

  const fetchFindingFilterOptions = async () => {
    const projectId: number = projectContext.project
      ? projectContext.project.id
      : 0;
    const data: any =
      await correctiveActionsFindingsApi.getFindingFilterOptions(projectId);

    const options = await data
      .map((_: { type: string }) => _.type)
      .sort()
      .map((type: any) => {
        return {
          label:
            type === 'CA'
              ? 'Corrective Action'
              : type === 'A'
              ? 'Achievement'
              : type === 'MI'
              ? 'Maintenance Item'
              : type,
          value: type,
        };
      });

    setFindingFilterOptions(options);
  };

  const fetchInspectionTemplateTypes = async () => {
    const projectId: number = projectContext.project
      ? projectContext.project.id
      : 0;
    const data: any =
      await correctiveActionsFindingsApi.getInspectionTemplateTypes(projectId);

    setInspectionTemplateTypeOptions(
      data.map((type: any) => {
        return {
          label: type.name,
          value: type.id,
        };
      }),
    );
    setTemplateTypes(
      data.map((type: any) => {
        return type.id;
      }),
    );
  };

  const fetchPageModelTotalCount = async () => {
    const projectId: number = projectContext.project
      ? projectContext.project.id
      : 0;
    const data: any =
      await correctiveActionsFindingsApi.getFindingModelPageCount(
        projectId,
        filter,
        status,
        templateTypes,
        page,
      );

    setTotalFindingModelCount(Number(data.total_model_count));
  };

  const onChangePerPageDataCount = async (dataCount: number) => {
    setperPageDataCount(dataCount);
  };

  useEffect(() => {
    fetchFindings();
    fetchFindingFilterOptions();
    fetchInspectionTemplateTypes();
    fetchPageModelTotalCount();
  }, []);

  useEffect(() => {
    fetchFindings();
  }, [page, perPageDataCount]);

  useEffect(() => {
    fetchFindings();
    fetchPageModelTotalCount();
  }, [filter, status, templateTypes]);

  if (!correctiveActionsQuery) {
    return <Loading what='corrective actions' />;
  }

  return (
    <div className='inspection-findings'>
      <div className='right-aligned'>
        <FormContextProvider>
          {
            inspectionTemplateTypeOptions && (
              <InspectionTemplateTypeFilter
                handleChange={setTemplateTypes}
                options={inspectionTemplateTypeOptions}
                selectedValues={templateTypes}
              />
            )}
          {findingFilterOptions && (
            <FindingFilter
              handleChange={setFilter}
              options={findingFilterOptions}
            />
          )}
          <FindingStatusFilter
            handleChange={setStatus}
            options={findingStatusOptions}
          />
        </FormContextProvider>
      </div>
      <section className='pure-g findings-listing'>
        {!correctiveActionsQuery.length ? (
          <p className='pure-u'>No findings</p>
        ) : (
          correctiveActionsQuery.map((correctiveActionFinding: any) => (
            <FindingCard
              key={correctiveActionFinding.id}
              finding={correctiveActionFinding}
            />
          ))
        )}
      </section>

      <section>
        <div className='pagination-container'>
          <Pagination
            activePage={page}
            itemsCountPerPage={perPageDataCount}
            totalItemsCount={totalFindingModelCount}
            pageRangeDisplayed={4}
            onChange={(e) => setPageValue(e)}
            itemClass='pagination-item'
          />

          {totalFindingModelCount > 0 ? (
            <select
              value={perPageDataCount}
              onChange={(e) =>
                onChangePerPageDataCount(parseInt(e.target.value))
              }
              className='pagesize-item'
            >
              {['10', '25', '50'].map((val, index) => {
                return (
                  <option key={index} value={val}>
                    {val}
                  </option>
                );
              })}
            </select>
          ) : null}
        </div>
      </section>
    </div>
  );
}
